(ns client-portal.client.system-configuration.views
  (:require [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.cohorts.data]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.layout :as l]
            [client-portal.client.locations.data]
            [client-portal.client.system-configuration.changes.views :as changes]
            [client-portal.client.system-configuration.data :as sc.d]
            [client-portal.client.system-configuration.fields.views :as fields]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.amelia-light.components.components :as alc]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(style/add-css "@keyframes sc-save-bar-in { from { opacity: 0; transform: translateY(8px); } }"
               "@media (prefers-reduced-motion: no-preference) { .sc-save-bar { animation: sc-save-bar-in 160ms ease-out; } }"
               ;; The namespace panel sits beside the content on wide screens and becomes a scrolling row above it on narrow
               ".sc-config-layout { display: flex; flex-direction: column; min-height: 100%; }"
               ".sc-namespace-panel { border-style: solid; border-width: 0 0 1px 0; flex-shrink: 0; }"
               ".sc-namespace-list { display: flex; gap: 4px; overflow-x: auto; padding: 12px 16px; }"
               ".sc-namespace-list > button { flex-shrink: 0; white-space: nowrap; }"
               "@media (min-width: 768px) {"
               " .sc-config-layout { flex-direction: row; }"
               " .sc-namespace-panel { border-width: 0 1px 0 0; width: 232px; }"
               " .sc-namespace-list { flex-direction: column; overflow-x: visible; padding: 16px 12px; position: sticky; top: 0; }"
               " .sc-namespace-list > button { white-space: normal; } }")

(defn namespace-panel [_]
  ;; In the narrow scrolling row the selected configuration can start out of view
  (let [scroll-to-selected (fn [this]
                             (some-> (re/dom-node this)
                                     (.querySelector "[aria-current=page]")
                                     (.scrollIntoView #js {:block "nearest"
                                                           :inline "nearest"})))]
    (re/create-class
      {:component-did-mount scroll-to-selected
       :component-did-update scroll-to-selected
       :reagent-render
       (fn [{:keys [namespace]}]
         [:nav {:aria-label "Configurations"
                :class-name "sc-namespace-panel"
                :style {:border-color (style/alc-color "Border/divider_secondary")}}
          (into [:div {:class-name "sc-namespace-list"}]
                (for [n (util/when-not-loading @(re/subscribe [:system-configuration/namespaces-]))
                      :let [active? (= n namespace)]]
                  ^{:key n}
                  [:button {:type "button"
                            :aria-current (when active? "page")
                            :class-name (str "rounded-md text-left outline-none transition-colors focus-visible:ring-[3px] "
                                             "focus-visible:ring-ring/50 hover:bg-up-background-hover"
                                             (when active? " bg-up-background-hover"))
                            :style {:border (str "1px solid " (if active? (style/alc-color "Border/divider") "transparent"))
                                    :color (style/alc-color (if active? "Text/primary_header" "Text/primary"))
                                    :cursor :pointer
                                    :font-size 14
                                    :font-weight (if active? 500 400)
                                    :line-height 1.43
                                    :padding "7px 12px"}
                            :on-click #(re/dispatch [:router/go-with-merged-query-params {} {:new-route [:configuration {:namespace n}]}])}
                   (sc.d/namespace-label n)]))])})))

(defn namespace-notice [{:keys [namespace concept-id]}]
  (when (= namespace "sales_smart_ordering_demo")
    (let [demo-link (str "http://realtime-server.synq3-staging.com/ocb_display/" concept-id)
          text-style {:color (style/alc-color "Text/primary_const")
                      :font-size 14
                      :line-height 1.43}]
      [:div {:style {:background-color (style/alc-color "Background/alert_const")
                     :border-radius 8
                     :display :flex
                     :flex-direction :column
                     :gap 4
                     :padding "12px 16px"
                     :max-width 960}}
       [:span {:style text-style}
        "This only drives sales demos. It doesn't change any real Smart Ordering configuration."]
       (when concept-id
         [:span {:style text-style}
          "Demo for this concept: "
          [:a {:href demo-link
               :target "_blank"
               :rel "noopener noreferrer"
               :style {:text-decoration :underline}}
           demo-link]])])))

(defn scope-stop [{:keys [active?]} & contents]
  (into [:div {:style {:background-color (when active? (style/alc-color "Background/active_secondary"))
                       :border-radius 8
                       :box-shadow (when active? (str "inset 0 0 0 1px " (style/alc-color "Common/active")))
                       :max-width 300}}]
        contents))

(defn scope-chevron []
  [alc/up-icon {:icon-type :ChevronRightIcon
                :aria-hidden true
                :style {:color (style/alc-color "Border/secondary_inp")}}])

(defn- dimensions
  "Cohorts and locations the concept actually has (an ingress identifier hangs off a location). A selected value keeps
   its stop so it can still be cleared."
  [{:keys [namespace concept-id cohort-id location-id]}]
  (let [cohorts (util/when-not-loading @(re/subscribe [:cohorts/by-concept-id- concept-id]))
        locations (util/when-not-loading @(re/subscribe [:locations/by-concept-id- concept-id]))
        scoped? (not (get-in sc.d/namespace-options [namespace :concept-only?]))]
    {:cohorts cohorts
     :locations locations
     :cohorts? (and scoped? (or (some? cohort-id) (boolean (not-empty cohorts))))
     :locations? (and scoped? (or (some? location-id) (boolean (not-empty locations))))}))

(defn scope-path
  "The concept → cohort → location → ingress identifier chain; the deepest chosen stop is the level being edited"
  []
  (let [{:keys [concept-id cohort-id location-id ingress-identifier] :as scope} @(re/subscribe [:system-configuration/scope-])
        {:keys [cohorts locations cohorts? locations?]} (dimensions scope)
        level (sc.d/scope-level scope)
        concepts @(re/subscribe [:concepts/list-data-])
        concept (concepts.d/concept-id+list->concept concept-id concepts)
        cohort-location-ids (some->> cohorts
                                     (some #(when (= (:id %) cohort-id) %))
                                     :location-ids
                                     (set))
        select #(re/dispatch [:router/go-with-merged-query-params %])]
    (into [:nav {:aria-label "Configuration level"
                 :style {:display :flex
                         :flex-wrap :wrap
                         :align-items :center
                         :gap 4
                         :max-width 960}}]
          (interpose
            [scope-chevron]
            (remove nil? [[scope-stop {:active? (and concept (= level :concept))}
                            ;; Picking a concept, even the current one, lands on its defaults
                            [alc/up-popover-button-dropdown {:placeholder "Choose a Concept"
                                                             :image-url (some-> (or (some-> concept :brand-configuration :logo-url)
                                                                                    (some-> concept :id (concepts.d/concept-logo-url)))
                                                                                (anon.d/logo-url))
                                                             :fixed-icon-type :LayersIcon
                                                             :search? true
                                                             :search-placeholder "Search concepts"
                                                             :no-results-message "No concepts found"
                                                             :align :start
                                                             :loading (util/loading? concepts)
                                                             :options (mapv (fn [{:keys [id] :as c}]
                                                                              {:text (anon.d/concept-label c)
                                                                               :value id
                                                                               :selected? (= id concept-id)})
                                                                            (util/when-not-loading concepts))
                                                             :on-change #(select {:sc-cid %
                                                                                  :sc-coid nil
                                                                                  :sc-lid nil
                                                                                  :sc-iid nil})}]]
                          (when cohorts?
                            [scope-stop {:active? (= level :cohort)}
                             [alc/up-popover-button-dropdown {:placeholder "Choose a Cohort"
                                                              :fixed-icon-type :UserGroupsIcon
                                                              :search? true
                                                              :search-placeholder "Search cohorts"
                                                              :no-results-message "No cohorts found"
                                                              :align :start
                                                              :clearable? true
                                                              :options (mapv (fn [{:keys [id label cohort-type-label]}]
                                                                               {:text (str label " (" cohort-type-label ")")
                                                                                :value id
                                                                                :selected? (= id cohort-id)})
                                                                             cohorts)
                                                              :on-change #(select {:sc-coid %
                                                                                   :sc-lid nil
                                                                                   :sc-iid nil})}]])
                          (when locations?
                            [scope-stop {:active? (= level :location)}
                             [alc/up-popover-button-dropdown {:placeholder "Choose a Location"
                                                              :fixed-icon-type :LocationIcon
                                                              :search? true
                                                              :search-placeholder "Search locations"
                                                              :no-results-message "No locations found"
                                                              :align :start
                                                              :clearable? true
                                                              :options (->> locations
                                                                            (filter #(or (nil? cohort-location-ids)
                                                                                         (contains? cohort-location-ids (:id %))))
                                                                            (mapv (fn [{:keys [id label]}]
                                                                                    {:text (anon.d/location-label label)
                                                                                     :value id
                                                                                     :selected? (= id location-id)})))
                                                              :on-change #(select {:sc-lid %
                                                                                   :sc-iid nil})}]])
                          (when locations?
                            [scope-stop {:active? (= level :ingress-identifier)}
                             [alc/up-popover-button-dropdown {:placeholder "Choose an Ingress Identifier"
                                                              :fixed-icon-type :RouteIcon
                                                              :search? true
                                                              :search-placeholder "Search or add"
                                                              :no-results-message "None for this location yet"
                                                              :align :start
                                                              :clearable? true
                                                              :disabled (not location-id)
                                                              :options (->> (cond-> (set (util/when-not-loading @(re/subscribe [:system-configuration/ingress-identifiers-])))
                                                                                    ingress-identifier (conj ingress-identifier))
                                                                            (sort)
                                                                            (mapv (fn [v]
                                                                                    {:text v
                                                                                     :value v
                                                                                     :selected? (= v ingress-identifier)})))
                                                              :on-change #(select {:sc-iid %})
                                                              :on-add-new #(select {:sc-iid %})}]])])))))

(defn- scope-names [{:keys [concept-id cohort-id location-id]} {:keys [cohorts locations]}]
  {:concept-name (some->> @(re/subscribe [:concepts/list-data-])
                          (concepts.d/concept-id+list->concept concept-id)
                          (anon.d/concept-label))
   :cohort-name (some #(when (= (:id %) cohort-id) (:label %)) cohorts)
   :location-name (some #(when (= (:id %) location-id) (anon.d/location-label (:label %))) locations)})

(defn scope-summary [{:keys [hidden-fields]}]
  (let [{:keys [location-id ingress-identifier] :as scope} @(re/subscribe [:system-configuration/scope-])
        {:keys [cohorts cohorts? locations?] :as dims} (dimensions scope)
        level (sc.d/scope-level scope)
        {:keys [overridden total]} @(re/subscribe [:system-configuration/override-counts-])
        {:keys [concept-name cohort-name location-name]} (scope-names scope dims)
        inheritors (cond-> []
                           cohorts? (conj "cohorts")
                           locations? (conj "locations" "ingress identifiers"))
        [editing inherits-from] (case level
                                  :concept [(str "Editing the defaults for " (or concept-name "this concept"))
                                            (if (not-empty inheritors)
                                              (str (s/capitalize (if (= (count inheritors) 1)
                                                                   (first inheritors)
                                                                   (str (s/join ", " (butlast inheritors)) " and " (last inheritors))))
                                                   " use these unless they override them.")
                                              "These are the values this concept uses.")]
                                  :cohort [(str "Editing " (or cohort-name "this cohort"))
                                           "the concept"]
                                  :location [(str "Editing " (or location-name "this location"))
                                             (if (some #(some #{location-id} (:location-ids %)) cohorts)
                                               "the concept and the location's cohorts"
                                               "the concept")]
                                  :ingress-identifier [(str "Editing " ingress-identifier)
                                                       (or location-name "its location")])]
    [:div {:style {:display :flex
                   :flex-wrap :wrap
                   :align-items :flex-end
                   :justify-content :space-between
                   :gap 12
                   :max-width 960}}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 4}}
      [:span {:style {:color (style/alc-color "Text/primary_header")
                      :font-size 15
                      :font-weight 500
                      :line-height 1.4}}
       editing]
      [:span {:style {:color (style/alc-color "Text/secondary")
                      :font-size 13
                      :line-height 1.45}}
       (if (= level :concept)
         inherits-from
         (str overridden " of " total (if (= total 1) " setting" " settings")
              " overridden here. The rest come from " inherits-from "."))]]
     (when (not= level :concept)
       [:div {:style {:display :flex
                      :gap 4}}
        [alc/up-ghost-button {:icon-type :UndoIcon
                              :style {:height 32}
                              :disabled (zero? overridden)
                              :on-click #(re/dispatch [:system-configuration/set-all-fields false hidden-fields])}
         "Inherit All"]
        [alc/up-ghost-button {:icon-type :EditIcon
                              :style {:height 32}
                              :disabled (= overridden total)
                              :on-click #(re/dispatch [:system-configuration/set-all-fields true hidden-fields])}
         "Override All"]])]))

(defn save-bar []
  (let [unsaved-count @(re/subscribe [:system-configuration/unsaved-count-])
        valid? @(re/subscribe [:system-configuration/valid?-])]
    (when (pos? unsaved-count)
      [:div {:class-name "sc-save-bar"
             :style {:background-color (style/alc-color "Background/card_hex")
                     :border-top (str "1px solid " (style/alc-color "Border/divider"))
                     :bottom 0
                     :display :flex
                     :flex-wrap :wrap
                     :align-items :center
                     :justify-content :flex-end
                     :gap 8
                     :padding "12px 0"
                     :position :sticky
                     :z-index (style/z :low)
                     :max-width 960}}
       [:span {:role "status"
               :style {:color (style/alc-color (if valid? "Text/secondary" "Common/error"))
                       :flex 1
                       :font-size 13
                       :line-height 1.4}}
        (if valid?
          (str unsaved-count (if (= unsaved-count 1) " unsaved change" " unsaved changes"))
          "Fix the settings marked below to save")]
       [alc/up-ghost-button {:on-click #(re/dispatch [:dialog/show [:system-configuration/review-changes]])}
        "Review Changes"]
       [:span {:aria-hidden true
               :style {:background-color (style/alc-color "Border/divider")
                       :height 20
                       :width 1}}]
       [alc/up-ghost-button {:on-click #(re/dispatch [:system-configuration/discard-changes])}
        "Discard"]
       [alc/up-call-to-action-button {:loading? @(re/subscribe [:system-configuration/saving?-])
                                      :disabled (not valid?)
                                      :on-click #(re/dispatch [:system-configuration/save])}
        "Save Changes"]])))

(defmethod alc/dialog-view
  :system-configuration/review-changes [_ _]
  (let [{:keys [ingress-identifier] :as scope} @(re/subscribe [:system-configuration/scope-])
        {:keys [concept-name cohort-name location-name]} (scope-names scope (dimensions scope))
        valid? @(re/subscribe [:system-configuration/valid?-])]
    [alc/dialog {:title "Review Changes"
                 :description (->> [concept-name cohort-name location-name ingress-identifier]
                                   (remove nil?)
                                   (s/join " › ")
                                   (str "Saves to "))
                 :footer [:<>
                          [alc/up-call-to-action-button {:disabled (not valid?)
                                                         :on-click #(do (re/dispatch [:dialog/hide])
                                                                        (re/dispatch [:system-configuration/save]))}
                           "Save Changes"]
                          [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                           "Keep Editing"]]}
     [:div {:style {:max-width "100%"
                    :width (if @(re/subscribe [:core/mobile-layout?-]) "100%" 720)}}
      [changes/diff-view @(re/subscribe [:system-configuration/changes-])]]]))

(defn scope-form [{:keys [scope]}]
  (re/create-class
    {:component-did-mount #(re/dispatch [:system-configuration/clear-scope scope])
     :component-will-unmount #(re/dispatch [:system-configuration/clear-scope scope])
     :reagent-render
     (fn [{:keys [scope fields hidden-fields]}]
       (let [stored @(re/subscribe [:system-configuration/stored- scope])
             inherited @(re/subscribe [:system-configuration/inherited- scope])]
         (cond
           (= stored :error)
           [empty-state/no-data-card {:text "This configuration couldn't be loaded. Reload the page to try again."
                                      :material-icon-type :error-outline}]

           (or (not @(re/subscribe [:system-configuration/form-ready?- scope]))
               (util/nil-or-loading? inherited))
           [empty-state/loading-card {:label "Loading configuration…"}]

           :else
           [:div {:style {:display :flex
                          :flex 1
                          :flex-direction :column
                          :gap 16}}
            [scope-summary {:hidden-fields hidden-fields}]
            ;; Hidden fields are only left unrendered; the save still walks them, so their loaded values are kept
            (if-let [visible-fields (not-empty (remove #(contains? hidden-fields (:field-name %)) fields))]
              (into [:div {:style {:border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                   :max-width 960}}]
                    (for [field visible-fields]
                      ^{:key (:field-name field)}
                      [fields/field-row field]))
              [empty-state/no-data-card {:text "Nothing in this configuration can be edited here"
                                         :material-icon-type :tune}])
            [save-bar]])))}))

(defn namespace-configuration [{:keys [namespace]}]
  (let [fields @(re/subscribe [:system-configuration/schema- namespace])
        scope @(re/subscribe [:system-configuration/scope-])
        {:keys [hidden-fields]} (get sc.d/namespace-options namespace)]
    [:div {:style {:display :flex
                   :flex 1
                   :flex-direction :column
                   :gap 20
                   :width "100%"}}
     [namespace-notice scope]
     (cond
       (not (:concept-id scope)) [empty-state/no-data-card {:text "Choose a concept to configure"
                                                            :material-icon-type :tune}]
       (util/nil-or-loading? fields) [empty-state/loading-card {:label "Loading configuration…"}]
       (empty? fields) [empty-state/no-data-card {:text "This configuration has no settings defined yet"}]
       :else ^{:key (pr-str scope)} [scope-form {:scope scope
                                                 :fields fields
                                                 :hidden-fields (or hidden-fields #{})}])]))

(defn root [_]
  (let [namespaces- (re/subscribe [:system-configuration/namespaces-])]
    (fn [{:keys [namespace] :as args}]
      (let [namespaces @namespaces-
            known-namespace? (some #{namespace} (util/when-not-loading namespaces))]
        [l/logged-in-with-header-nav-core (merge args {:header-filters? false
                                                       :concept-dropdown? false
                                                       :location-dropdown? false
                                                       :crew-type-radio? false
                                                       :timeframe-dropdown? false})
         [:div {:class-name "sc-config-layout"}
          [namespace-panel {:namespace namespace}]
          [:div {:style {:display :flex
                         :flex 1
                         :flex-direction :column
                         :min-width 0}}
           [l/standard-content-container {:title (sc.d/namespace-label namespace)
                                          :title-style {:gap 16}
                                          :header-contents (when known-namespace?
                                                             [scope-path])
                                          :sticky-header? true
                                          :container-style {:flex 1
                                                            :min-height :auto}}
            (cond
              (util/nil-or-loading? namespaces) [empty-state/loading-card {:label "Loading configuration…"}]
              (not known-namespace?) [empty-state/no-data-card {:text (str "There's no configuration named \"" namespace
                                                                           "\". Choose one from the list.")}]
              :else [namespace-configuration {:namespace namespace}])]]]]))))
